// PaymentReport.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  useTheme,
  useColorMode,
  Select,
  Button,
} from "@chakra-ui/react";
import moment from "moment";
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlassDollar,
} from "@fortawesome/free-solid-svg-icons";
import { cqAPI } from "../../lib/Auth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./PaymentReport.css";
import { useReactToPrint } from "react-to-print";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function PaymentReport() {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const getStatus = (payrequest) => {
    if (payrequest.cancelled) {
      return "Cancelled";
    } else if (payrequest.paid) {
      return "Paid";
    } else {
      return "Pending";
    }
  };

  const getUpdateDate = (payrequest) => {
    if (payrequest.cancelled) {
      return moment(payrequest.cancelled).format("MMM D, YYYY h:mm a");
    } else if (payrequest.paid) {
      return moment(payrequest.paid).format("MMM D, YYYY h:mm a");
    } else {
      return "";
    }
  };

  const getUpdateTimestamp = (payrequest) => {
    if (payrequest.cancelled) {
      return new Date(payrequest.cancelled);
    } else if (payrequest.paid) {
      return new Date(payrequest.paid);
    } else {
      return payrequest.created ? new Date(payrequest.created) : new Date();
    }
  };

  const getSortValue = (payment, key) => {
    switch (key) {
      case "created":
        return new Date(payment.created);
      case "patient":
        return payment.patient.name;
      case "amount":
        return payment.payrequest.amount;
      case "status":
        return getStatus(payment.payrequest);
      case "updatedDate":
        return getUpdateTimestamp(payment.payrequest) || new Date(payment.created);
      default:
        return payment[key];
    }
  };

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...payments];
    if (sortConfig !== null && sortConfig.key) {
      sortableItems.sort((a, b) => {
        const va = getSortValue(a, sortConfig.key);
        const vb = getSortValue(b, sortConfig.key);
        if (va < vb) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (va > vb) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [payments, sortConfig]);
  const showLocationColumn = sortedTransactions.some((payment) => payment.location_name);

  const fetchPayments = async (params = {}) => {
    const { startDate, endDate } = params;
    setIsLoading(true);

    try {
      // Use parameters if provided, otherwise fallback to component state
      const formattedStartDate = startDate
        ? moment(startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = endDate
        ? moment(endDate).format("L")
        : moment(dateRange[1]).format("L");

      const response = await cqAPI.get(
        `/reports/payrequests?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}`
      );

      if (response.type === "ok") {
        setPayments(response.data);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let start, end;
    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      case "Custom":
      default:
        start = null;
        end = null;
    }
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const updateUrlHash = (startDate, endDate, selectedRange) => {
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.location.hash = queryParams;
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex
      direction="column"
      width="100%"
      paddingLeft="20px"
      paddingRight="20px"
    >
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
          mr={2}
        >
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingTop="10px"
      >
        <Box
          as="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </Box>
        <Box
          as="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1)); // Remove the '#' part
    const startDate = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const endDate = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const selectedRange = hashParams.get("selectedRange") || "Today";

    setDateRange([startDate, endDate]);
    setSelectedRange(selectedRange);

    fetchPayments({
      startDate,
      endDate,
      selectedRange,
    });
  }, []);

  useEffect(() => {
    updateUrlHash(startDate, endDate, selectedRange);
  }, [startDate, endDate, selectedRange]);

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="flex-start" mb={4}>
        <Flex align="center" mr={2}>
          <Select
            placeholder="Select range"
            value={selectedRange}
            onChange={(e) => handleDateRangeSelection(e.target.value)}
            className="date-type"
          >
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="Last 7 days">Last 7 days</option>
            <option value="Last 4 weeks">Last 4 weeks</option>
            <option value="Month to Date">Month to Date</option>
            <option value="Quarter to date">Quarter to date</option>
            <option value="Year to date">Year to date</option>
            <option value="Custom">Custom</option>
          </Select>
        </Flex>
        <Flex align="center" mr={2}>
          <DatePicker
            className={`custom-datepicker ${
              colorMode === "light" ? "light-theme" : "dark-theme"
            }`}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
              setSelectedRange("Custom"); // Ensure custom range is selected when dates are manually changed
            }}
            isClearable={true}
            renderCustomHeader={renderCustomHeader}
          />
        </Flex>
        <Flex marginLeft="auto">
          <Button
            leftIcon={<FontAwesomeIcon icon={faMagnifyingGlassDollar} />}
            onClick={fetchPayments}
          >
            Show Payment Requests
          </Button>
          <Button ml={2} onClick={handlePrint} className="no-print">
            Print
          </Button>
        </Flex>
      </Flex>

      <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />

      <div ref={componentRef}>
        <VStack pacing={0} align="stretch">
          <Flex borderBottom="1px" borderColor="gray.200" p={2}>
            <HStack spacing={8} flex="1">
              <Text
                fontSize="sm"
                flexBasis="20%"
                fontWeight="bold"
                onClick={() => requestSort("created")}
                cursor="pointer"
              >
                Date Sent
              </Text>
              {showLocationColumn && (
                <Text fontSize="sm" flexBasis="15%" fontWeight="bold">
                  Location
                </Text>
              )}
              <Text
                fontSize="sm"
                flexBasis="20%"
                fontWeight="bold"
                onClick={() => requestSort("patient")}
                cursor="pointer"
              >
                Patient
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("amount")}
                cursor="pointer"
              >
                Amount
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("status")}
                cursor="pointer"
              >
                Status
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("updatedDate")}
                cursor="pointer"
              >
                Update Date
              </Text>
            </HStack>
          </Flex>
          {sortedTransactions.map((payment) => (
            <Flex key={payment._id} p={2}>
              <HStack spacing={8} flex="1">
                <Text fontSize="sm" flexBasis="20%">
                  {moment(payment.created).format("MMM D, YYYY h:mm a")}
                </Text>
                {showLocationColumn && (
                  <Text fontSize="sm" flexBasis="15%">
                    {payment.location_name || "-"}
                  </Text>
                )}
                <Text fontSize="sm" flexBasis="20%">
                  {payment.patient.name}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {(payment.payrequest.amount / 100).toFixed(2)}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {getStatus(payment.payrequest)}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {getUpdateDate(payment.payrequest)}
                </Text>
              </HStack>
            </Flex>
          ))}
        </VStack>
        <Box textAlign="right">
          <Text>
            Total: $
            {(
              payments.reduce((acc, cur) => {
                acc += cur.payrequest.amount;
                return acc;
              }, 0) / 100
            ).toFixed(2)}
          </Text>
        </Box>
      </div>
    </Box>
  );
}

export default PaymentReport;
